/**
 * Custom Premium Sticky Header
 * File: assets/custom-header.js
 * Fixed: Variable scope issues, announcement bar offset
 */

(function () {
  'use strict';

  // ── Selectors ──────────────────────────────────────────────────────────────
  const header       = document.getElementById('custom-header');
  const mobileMenu   = document.getElementById('custom-mobile-menu');
  const hamburgerBtn = document.getElementById('custom-hamburger-btn');
  const closeBtn     = document.getElementById('custom-menu-close');
  const overlay      = document.getElementById('custom-menu-overlay');

  if (!header) return;

  // ── State ──────────────────────────────────────────────────────────────────
  let isMenuOpen = false;
  let scrollTicking = false;
  let previouslyFocused = null;
  const SCROLL_THRESHOLD = 10;

  // ── Announcement Bar Height ────────────────────────────────────────────────
  function getAnnouncementBarHeight() {
    const selectors = [
      '.custom-announcement-section',
      '.custom-ann-bar',
      '[id*="custom-announcement"]',
      '.custom-announcement-bar',
    ];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el.getBoundingClientRect().height;
    }
    return 0;
  }

  function applyHeaderOffset() {
    const annHeight = getAnnouncementBarHeight();
    const root = document.documentElement;
    root.style.setProperty('--ch-announcement-height', `${annHeight}px`);
    const headerHeight = header.getBoundingClientRect().height;
    root.style.setProperty('--ch-total-offset', `${annHeight + headerHeight}px`);
  }

  // ── Scroll Behavior ────────────────────────────────────────────────────────
  function updateScrollState() {
    const scrollY = window.scrollY;
    const isTransparent = header.dataset.transparent === 'true';
    const annHeight = getAnnouncementBarHeight();
    const threshold = Math.max(SCROLL_THRESHOLD, annHeight);

    if (scrollY > threshold) {
      header.classList.add('is-scrolled');
    } else {
      header.classList.remove('is-scrolled');
    }
    scrollTicking = false;
  }

  function onScroll() {
    if (!scrollTicking) {
      window.requestAnimationFrame(updateScrollState);
      scrollTicking = true;
    }
  }

  // ── Mobile Menu Open / Close ───────────────────────────────────────────────
  function openMenu() {
    isMenuOpen = true;
    previouslyFocused = document.activeElement;
    mobileMenu.classList.add('is-open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    hamburgerBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    setDrawerTabIndex(0);
    requestAnimationFrame(() => {
      const first = getDrawerFocusables()[0];
      first?.focus();
    });
    document.addEventListener('keydown', handleMenuKeydown);
  }

  function closeMenu() {
    isMenuOpen = false;
    mobileMenu.classList.remove('is-open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    hamburgerBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setDrawerTabIndex(-1);
    previouslyFocused?.focus();
    previouslyFocused = null;
    document.removeEventListener('keydown', handleMenuKeydown);
    closeAllAccordions();
  }

  function toggleMenu() { isMenuOpen ? closeMenu() : openMenu(); }

  // ── Focus Trap ─────────────────────────────────────────────────────────────
  function getDrawerFocusables() {
    return Array.from(mobileMenu.querySelectorAll(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), [tabindex="0"]'
    ));
  }

  function setDrawerTabIndex(val) {
    mobileMenu.querySelectorAll('a[href], button:not([disabled])')
      .forEach(el => el.setAttribute('tabindex', val));
  }

  function handleMenuKeydown(e) {
    if (e.key === 'Escape') { closeMenu(); return; }
    if (e.key === 'Tab') {
      const focusables = getDrawerFocusables();
      if (!focusables.length) return;
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    }
  }

  // ── Mobile Accordions ──────────────────────────────────────────────────────
  const accordionTriggers = document.querySelectorAll('[data-mobile-accordion]');

  accordionTriggers.forEach(trigger => {
    trigger.addEventListener('click', function () {
      const isExpanded = this.getAttribute('aria-expanded') === 'true';
      const submenu = this.nextElementSibling;
      if (!submenu) return;
      accordionTriggers.forEach(other => {
        if (other !== this) {
          other.setAttribute('aria-expanded', 'false');
          const otherSub = other.nextElementSibling;
          otherSub?.classList.remove('is-open');
          otherSub?.setAttribute('aria-hidden', 'true');
        }
      });
      this.setAttribute('aria-expanded', String(!isExpanded));
      submenu.classList.toggle('is-open', !isExpanded);
      submenu.setAttribute('aria-hidden', String(isExpanded));
    });
  });

  function closeAllAccordions() {
    accordionTriggers.forEach(t => {
      t.setAttribute('aria-expanded', 'false');
      const sub = t.nextElementSibling;
      sub?.classList.remove('is-open');
      sub?.setAttribute('aria-hidden', 'true');
    });
  }

  // ── Desktop Dropdowns ──────────────────────────────────────────────────────
  const dropdownTriggers = document.querySelectorAll('[data-dropdown-trigger]');

  dropdownTriggers.forEach(trigger => {
    const wrap     = trigger.closest('.custom-header__dropdown-wrap');
    const dropdown = wrap?.querySelector('.custom-header__dropdown');
    if (!dropdown) return;

    let closeTimeout;

    function openDropdown() {
      clearTimeout(closeTimeout);
      closeAllDropdowns(trigger);
      trigger.setAttribute('aria-expanded', 'true');
      dropdown.classList.add('is-open');
    }

    function scheduleClose() {
      closeTimeout = setTimeout(() => {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
      }, 150);
    }

    wrap.addEventListener('mouseenter', openDropdown);
    wrap.addEventListener('mouseleave', scheduleClose);
    dropdown.addEventListener('mouseenter', () => clearTimeout(closeTimeout));
    dropdown.addEventListener('mouseleave', scheduleClose);

    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.getAttribute('aria-expanded') === 'true' ? scheduleClose() : openDropdown();
      }
      if (e.key === 'Escape') {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
        trigger.focus();
      }
    });

    dropdown.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
        trigger.focus();
      }
    });
  });

  function closeAllDropdowns(except = null) {
    dropdownTriggers.forEach(t => {
      if (t === except) return;
      t.setAttribute('aria-expanded', 'false');
      const w  = t.closest('.custom-header__dropdown-wrap');
      const dd = w?.querySelector('.custom-header__dropdown');
      dd?.classList.remove('is-open');
    });
  }

  document.addEventListener('click', e => {
    if (!e.target.closest('.custom-header__dropdown-wrap')) closeAllDropdowns();
  });

  // ── Cart Count Live Update ─────────────────────────────────────────────────
  async function refreshCartCount() {
    try {
      const res  = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) return;
      const cart  = await res.json();
      const count = cart.item_count;
      const link  = document.getElementById('custom-header-cart');

      if (link) {
        let badge = link.querySelector('.custom-header__cart-count');
        if (count > 0) {
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'custom-header__cart-count';
            badge.setAttribute('aria-hidden', 'true');
            link.appendChild(badge);
          }
          badge.textContent = count;
        } else {
          badge?.remove();
        }
        link.setAttribute('aria-label', `Cart (${count} item${count !== 1 ? 's' : ''})`);
      }

      const mobileBadge = document.querySelector('.custom-mobile-menu__cart-badge');
      if (mobileBadge) {
        mobileBadge.textContent = count;
        mobileBadge.style.display = count > 0 ? '' : 'none';
      }
    } catch (_) {}
  }

  // ── Event Listeners ────────────────────────────────────────────────────────
  hamburgerBtn?.addEventListener('click', toggleMenu);
  closeBtn?.addEventListener('click', closeMenu);
  overlay?.addEventListener('click', closeMenu);
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', applyHeaderOffset, { passive: true });
  document.addEventListener('cart:updated', refreshCartCount);
  document.addEventListener('shopify:section:load', () => {
    applyHeaderOffset();
    refreshCartCount();
  });

  // Close on resize to desktop
  window.matchMedia('(min-width: 1024px)').addEventListener('change', e => {
    if (e.matches && isMenuOpen) closeMenu();
  });

  // ── Init ───────────────────────────────────────────────────────────────────
  updateScrollState();
  applyHeaderOffset();
  refreshCartCount();

  // Reapply offset after fonts/images load
  window.addEventListener('load', applyHeaderOffset);

})();
